import { useMemo, type PropsWithChildren } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { footerLinks, navItems } from "@/data/siteContent";
import { ThemeToggle } from "@/components/ThemeToggle";

export function Layout({ children }: PropsWithChildren) {
  const location = useLocation();
  const activeLabel = useMemo(() => {
    const active = navItems.find((item) => item.href === location.pathname);
    return active?.description ?? "Studio overview";
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-text">
      <div className="pointer-events-none fixed inset-0 bg-hero-radial opacity-80" />
      <div className="pointer-events-none fixed inset-0 grid-fade opacity-70" />
      <header className="sticky top-0 z-50 border-b border-outline/10 bg-background/80 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 md:px-8">
          <div className="space-y-1">
            <NavLink to="/" className="font-display text-2xl italic tracking-[0.14em] text-primary">
              ALETHEIA
            </NavLink>
            <p className="font-label text-[0.62rem] uppercase tracking-[0.28em] text-muted">{activeLabel}</p>
          </div>
          <div className="hidden items-center gap-3 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  [
                    "rounded-pill border px-4 py-2 font-label text-xs uppercase tracking-[0.22em] transition",
                    isActive
                      ? "border-tertiary/30 bg-tertiary/10 text-tertiary"
                      : "border-transparent bg-transparent text-muted hover:border-outline/20 hover:bg-surface-high hover:text-text",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </nav>
      </header>
      <main className="relative z-10">{children}</main>
      <footer className="relative z-10 border-t border-outline/10 bg-surface px-4 py-10 md:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <p className="font-display text-xl italic text-primary">Aletheia Development</p>
            <p className="max-w-xl text-sm leading-6 text-muted">
              Truth through software architecture, technical education, and experimental visual work.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {footerLinks.map((link) => (
              <span
                key={link.label}
                className={[
                  "rounded-pill border px-4 py-2 font-label text-xs uppercase tracking-[0.18em]",
                  link.state === "placeholder"
                    ? "border-outline/20 bg-surface-high text-muted"
                    : "border-primary/20 bg-primary/10 text-primary",
                ].join(" ")}
                title={link.note}
              >
                {link.label}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
